// askSmart-ui/src/pages/TOSP/occupancyDraft.ts
import {
  LeasedSpace,
  LeaseReviewStamp,
  LeaseSchedule,
  Occupancy,
  RentEscalation,
  RentStepUpMonth,
  newId,
} from "./types";
import { parseNumeric } from "./validation";

// Leases are edited inline on the Tenant form, so every scalar field is held as
// text until the tenant is saved — a half-typed number never has to round-trip
// through the model, and a blank stays blank rather than becoming 0.
//
// The draft mirrors the record's own shape: what the agreement states once sits
// on the occupancy draft, and what varies area by area sits on a leased space
// draft inside it.

/** A renewal row while the form is open. */
export interface RentResetDraft {
  id: string;
  rentCommenceDate: string;
  rentPsfCommencement: string;
  /** Read off an amendment rather than typed — see RentReset.fromAmendment. */
  fromAmendment?: boolean;
}

/** One leased space while the form is open. */
export interface LeasedSpaceDraft {
  id: string;
  name: string;
  unitId: string;
  rentCommenceDate: string;
  rentPsfCommencement: string;
  rentResets: RentResetDraft[];
  /** The one field on a space that isn't text — see the toggle on its card. */
  annualIncreaseEnabled: boolean;
  minimumIncrease: string;
  maximumIncrease: string;
  rentStepUpMonth: string;
  bmvaCap: string;
}

export type OccupancyDraft = {
  id: string;
  extensionTermEnabled: boolean;
  extensionTerms: string[];
  /** How many extension terms have been exercised, counted from the first. */
  extensionTermsUsed: number;
  leasedSpaces: LeasedSpaceDraft[];
  /** Carried through the form untouched — only the lease review writes it. */
  leaseReview: LeaseReviewStamp | null;
  /** Field keys an amendment changed; painted in the amendment blue. */
  amendedFields: string[];
  /** False only where the admin has switched sales reporting off. */
  salesReported: boolean;
  [key: string]:
    | string
    | number
    | boolean
    | string[]
    | LeasedSpaceDraft[]
    | LeaseReviewStamp
    | null;
};

/** Text fields on the lease itself — everything but the terms and the spaces. */
export const OCCUPANCY_TEXT_KEYS = [
  "landlordLegalEntity",
  "occupancyDate",
  "openingDate",
  "leaseCommenceDate",
  "leaseExecutionDate",
  "leaseTerm",
  "leaseEndDate",
  "percentageRentThresholdRate",
  "percentageRentPayment",
  "securityDepositAmount",
  "depositPaidDate",
  "bmvaPercentage",
  "bmvaCap",
  // Which SMRT Docs document holds this lease. Set the first time an admin
  // picks one, so a tenant with dozens of documents is asked once per lease
  // rather than on every review.
  "leaseDocumentKey",
  "notes",
  "rentRenewalNotations",
  "extensionNotes",
  "useClauseSummary",
  "exclusiveSummary",
] as const;

/** The lease's own fields that are amounts rather than rates or dates. */
const MONEY_KEYS = new Set<string>(["securityDepositAmount", "bmvaCap"]);

/** Text fields on a leased space. */
export const SPACE_TEXT_KEYS = [
  "name",
  "unitId",
  "rentCommenceDate",
  "rentPsfCommencement",
  "minimumIncrease",
  "maximumIncrease",
  "rentStepUpMonth",
  "bmvaCap",
] as const;

/** Reads a text field off a draft — the index signature is a union type. */
export const draftText = (draft: OccupancyDraft, key: string): string => {
  const value = draft[key];
  return typeof value === "string" ? value : "";
};

// ── The lease review's flat value channel ────────────────────────────────────
//
// The review carries one editable string per field, because that's what a
// reviewer confirms — a value they can read against the document. Extension
// terms aren't one string on the draft, they're a list and a toggle, so they're
// translated at both ends rather than the review growing a second kind of
// field for one term.

/** The extension terms as one editable line, e.g. "60 months, 60 months". */
export const extensionTermsText = (draft: OccupancyDraft): string =>
  draft.extensionTermEnabled
    ? draft.extensionTerms.map((term) => term.trim()).filter(Boolean).join(", ")
    : "";

/** What the review should show for a lease-wide field, whatever shape it's held in. */
export const reviewSeed = (draft: OccupancyDraft, key: string): string => {
  if (key === "extensionTerms") return extensionTermsText(draft);

  const own = draftText(draft, key);
  if (own.trim()) return own;

  // The rent rows on the review are the FIRST area's — rent commencement, the
  // rate, the escalation bracket and the step-up month are stated once against
  // the main commercial unit. They live on that leased space, not on the
  // occupancy, so reading the occupancy alone returns nothing for all five.
  //
  // It went unnoticed because a first review fills them from the document. It
  // shows up on a RE-review, where the record is the only source: an amendment
  // says nothing about the original rent, so the rows opened blank and the
  // terms confirmed last time looked lost.
  const first = draft.leasedSpaces?.[0];
  if (first && (SPACE_TEXT_KEYS as readonly string[]).includes(key)) {
    const held = (first as unknown as Record<string, unknown>)[key];
    return typeof held === "string" ? held : "";
  }

  return own;
};

/** The reviewed lease-wide values as a patch the draft can take. */
export function reviewPatch(values: Record<string, string>): Partial<OccupancyDraft> {
  const { extensionTerms, ...rest } = values;
  const patch: Partial<OccupancyDraft> = { ...rest };

  if (extensionTerms !== undefined) {
    const terms = extensionTerms
      .split(",")
      .map((term) => term.trim())
      .filter(Boolean);
    // Only ever adds. A blank line means the lease grants no extensions, which
    // is not the same as the admin wanting the ones they recorded switched off
    // — clearing them stays a deliberate act on the occupancy card.
    if (terms.length > 0) {
      patch.extensionTerms = terms;
      patch.extensionTermEnabled = true;
    }
  }

  return patch;
}

export function emptyLeasedSpaceDraft(name = ""): LeasedSpaceDraft {
  return {
    id: newId("spc"),
    name,
    unitId: "",
    rentCommenceDate: "",
    rentPsfCommencement: "",
    rentResets: [],
    annualIncreaseEnabled: true,
    minimumIncrease: "",
    maximumIncrease: "",
    rentStepUpMonth: "",
    bmvaCap: "",
  };
}

export function emptyOccupancyDraft(): OccupancyDraft {
  return OCCUPANCY_TEXT_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: "" }),
    {
      id: newId("occ"),
      extensionTermEnabled: false,
      extensionTerms: [],
      extensionTermsUsed: 0,
      // A lease demises at least one area, so a new one opens with a space
      // ready rather than with an empty list and a button to find.
      leasedSpaces: [emptyLeasedSpaceDraft()],
      leaseReview: null,
      amendedFields: [],
      // A new lease reports until somebody says otherwise.
      salesReported: true,
    } as OccupancyDraft,
  );
}

export const emptyRentReset = (): RentResetDraft => ({
  id: newId("rst"),
  rentCommenceDate: "",
  rentPsfCommencement: "",
});

const asText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * A stored amount as money reads: "16.50", "15,000.00".
 *
 * Numbers lose their shape on the way to a string — 16.50 comes back "16.5"
 * and 15000 comes back "15000" — so a record reopened a week later shows
 * figures the admin never typed. Only applied when a record is read INTO the
 * form; what they type after that is left exactly as typed, and parseNumeric
 * strips the separators again on the way back out.
 */
export const moneyText = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? ""
    : value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export function toLeasedSpaceDraft(space: LeasedSpace): LeasedSpaceDraft {
  return {
    id: space.id,
    name: space.name || "",
    unitId: space.unitId || "",
    rentCommenceDate: space.rentCommenceDate || "",
    rentPsfCommencement: moneyText(space.rentPsfCommencement),
    rentResets: (space.rentResets || []).map((reset) => ({
      id: reset.id,
      rentCommenceDate: reset.rentCommenceDate,
      rentPsfCommencement: moneyText(reset.rentPsfCommencement),
      fromAmendment: reset.fromAmendment,
    })),
    annualIncreaseEnabled: space.annualIncreaseEnabled ?? true,
    minimumIncrease: asText(space.minimumIncrease),
    maximumIncrease: asText(space.maximumIncrease),
    rentStepUpMonth: space.rentStepUpMonth || "",
    bmvaCap: moneyText(space.bmvaCap),
  };
}

export function fromLeasedSpaceDraft(draft: LeasedSpaceDraft): LeasedSpace {
  return {
    id: draft.id,
    name: draft.name.trim(),
    unitId: draft.unitId,
    rentCommenceDate: draft.rentCommenceDate,
    rentPsfCommencement: parseNumeric(draft.rentPsfCommencement),
    // Renewals with no date can't take effect, so they're dropped.
    rentResets: draft.rentResets
      .filter((reset) => reset.rentCommenceDate)
      .map((reset) => ({
        id: reset.id,
        rentCommenceDate: reset.rentCommenceDate,
        rentPsfCommencement: parseNumeric(reset.rentPsfCommencement),
        fromAmendment: reset.fromAmendment,
      })),
    // Figures typed and then switched off are dropped rather than kept out of
    // sight, so what's on the record is what the card shows.
    annualIncreaseEnabled: draft.annualIncreaseEnabled,
    minimumIncrease: draft.annualIncreaseEnabled
      ? parseNumeric(draft.minimumIncrease)
      : null,
    maximumIncrease: draft.annualIncreaseEnabled
      ? parseNumeric(draft.maximumIncrease)
      : null,
    rentStepUpMonth: draft.annualIncreaseEnabled
      ? (draft.rentStepUpMonth as RentStepUpMonth) || ""
      : "",
    bmvaCap: parseNumeric(draft.bmvaCap),
  };
}

export function toOccupancyDraft(occupancy: Occupancy): OccupancyDraft {
  return OCCUPANCY_TEXT_KEYS.reduce((acc, key) => {
    const value = occupancy[key as keyof Occupancy];
    // Amounts read back as money; rates and dates read back as themselves.
    return {
      ...acc,
      [key]: MONEY_KEYS.has(key)
        ? moneyText(value as number | null)
        : asText(value),
    };
  }, {
    id: occupancy.id,
    extensionTermEnabled: occupancy.extensionTermEnabled ?? false,
    extensionTerms: occupancy.extensionTerms ?? [],
    extensionTermsUsed: occupancy.extensionTermsUsed ?? 0,
    // Already read forward by normalizeOccupancy, so there is always at least
    // one — the fallback is for a record built by hand in a test.
    leasedSpaces: (occupancy.leasedSpaces?.length
      ? occupancy.leasedSpaces
      : []
    ).map(toLeasedSpaceDraft),
    leaseReview: occupancy.leaseReview ?? null,
    amendedFields: occupancy.amendedFields ?? [],
    // Absent on every lease written before the switch existed, and absent
    // means reporting — so it reads back as true rather than as nothing.
    salesReported: occupancy.salesReported !== false,
  } as OccupancyDraft);
}

export function fromOccupancyDraft(draft: OccupancyDraft): Occupancy {
  const text = (key: string) => draftText(draft, key);
  const num = (key: string) => parseNumeric(text(key));
  return {
    id: draft.id,
    leasedSpaces: draft.leasedSpaces.map(fromLeasedSpaceDraft),
    occupancyDate: text("occupancyDate"),
    openingDate: text("openingDate"),
    leaseCommenceDate: text("leaseCommenceDate"),
    leaseExecutionDate: text("leaseExecutionDate"),
    leaseTerm: text("leaseTerm").trim(),
    leaseEndDate: text("leaseEndDate"),
    extensionTermEnabled: draft.extensionTermEnabled,
    // Terms the user added but never filled in are dropped.
    extensionTerms: draft.extensionTermEnabled
      ? draft.extensionTerms.map((t) => t.trim()).filter(Boolean)
      : [],
    // Clamped to the terms that survive above, and zeroed when extensions are
    // off, so the used count can never point past the list it greys.
    extensionTermsUsed: draft.extensionTermEnabled
      ? Math.max(
          0,
          Math.min(
            draft.extensionTermsUsed ?? 0,
            draft.extensionTerms.map((t) => t.trim()).filter(Boolean).length,
          ),
        )
      : 0,
    percentageRentThresholdRate: num("percentageRentThresholdRate"),
    percentageRentPayment: num("percentageRentPayment"),
    securityDepositAmount: num("securityDepositAmount"),
    depositPaidDate: text("depositPaidDate"),
    bmvaPercentage: num("bmvaPercentage"),
    bmvaCap: num("bmvaCap"),
    landlordLegalEntity: text("landlordLegalEntity").trim(),
    notes: text("notes").trim(),
    rentRenewalNotations: text("rentRenewalNotations").trim(),
    extensionNotes: text("extensionNotes").trim(),
    useClauseSummary: text("useClauseSummary").trim(),
    exclusiveSummary: text("exclusiveSummary").trim(),
    leaseDocumentKey: text("leaseDocumentKey").trim(),
    leaseReview: draft.leaseReview ?? null,
    amendedFields: draft.amendedFields ?? [],
    salesReported: draft.salesReported !== false,
  };
}

/** The last completed review on a draft, if it has one. */
export const draftReview = (draft: OccupancyDraft): LeaseReviewStamp | null =>
  (draft.leaseReview as LeaseReviewStamp | null) ?? null;

/** The lease schedule as it stands in the form, for the live date figures. */
export const draftSchedule = (draft: OccupancyDraft): LeaseSchedule => ({
  leaseCommenceDate: draftText(draft, "leaseCommenceDate"),
  leaseTerm: draftText(draft, "leaseTerm"),
  leaseEndDate: draftText(draft, "leaseEndDate"),
  extensionTermEnabled: draft.extensionTermEnabled,
  extensionTerms: draft.extensionTerms,
  extensionTermsUsed: draft.extensionTermsUsed,
});

/**
 * One space's rent escalation as it stands in the form, for the live rates.
 *
 * The rent is the space's and the commencement date is the lease's, exactly as
 * on the saved record — so the card shows the same figures before a save as
 * after one.
 */
export const draftEscalation = (
  draft: OccupancyDraft,
  space: LeasedSpaceDraft,
): RentEscalation => ({
  rentCommenceDate: space.rentCommenceDate,
  leaseCommenceDate: draftText(draft, "leaseCommenceDate"),
  rentPsfCommencement: parseNumeric(space.rentPsfCommencement),
  rentResets: space.rentResets
    .filter((reset) => reset.rentCommenceDate)
    .map((reset) => ({
      id: reset.id,
      rentCommenceDate: reset.rentCommenceDate,
      rentPsfCommencement: parseNumeric(reset.rentPsfCommencement),
    })),
  // Off is a rate of zero, not a missing one — see spaceEscalation in types.
  minimumIncrease: space.annualIncreaseEnabled ? parseNumeric(space.minimumIncrease) : 0,
  maximumIncrease: space.annualIncreaseEnabled ? parseNumeric(space.maximumIncrease) : 0,
  rentStepUpMonth: space.annualIncreaseEnabled
    ? (space.rentStepUpMonth as RentStepUpMonth) || ""
    : "",
});
